import React, { useCallback, useState } from 'react'
import { makeStyles, useUsers } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { CalendarEvent } from 'paintscout'
import moment from 'moment'
import type { EventActivity, SearchActivitiesQuery } from '@paintscout/api'
import { useSaveEventMutation, useSearchActivitiesQuery } from '@paintscout/api'
import { mapActivitiesToCalendarEvents } from '@paintscout/util/builder'
import EventTable from './EventTable'

const useStyles = makeStyles<Theme, EventDocListProps>(
  (_theme) => ({
    root: {}
  }),
  { name: 'EventDocList' }
)

export interface EventDocListProps {
  classes?: StyleClasses<typeof useStyles>
  title?: string
  handleCreate?: () => void
  onEventsChange?: () => void
  query?: string
  perPage?: number
  hideSinglePagePagination?: boolean
}

function EventDocList(props: EventDocListProps) {
  const classes = useStyles(props)
  const { users } = useUsers()
  const { title, handleCreate, onEventsChange, query, perPage = 5 } = props

  const [saveEvent] = useSaveEventMutation()

  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])

  const { loading } = useSearchActivitiesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
    variables: {
      query: query,
      limit: 1000
    },
    onError: useCallback((error) => {
      console.error(error)
    }, []),
    onCompleted: useCallback((data: SearchActivitiesQuery) => {
      const calendarEvents = mapActivitiesToCalendarEvents(
        data.searchActivities.activities as EventActivity[],

        users || []
      )
      setCalendarEvents(sortEvents(calendarEvents))
    }, [])
  })

  const events = sortEvents(calendarEvents)
  const [page, setPage] = useState(1)
  const totalRows = events?.length

  const handleEditEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    let updateContact = false
    setCalendarEvents((prevEvents) =>
      prevEvents.map((prevEvent) => {
        if (prevEvent.id === event.id) {
          if (prevEvent?.contact?.preferredContactMethod !== event?.contact?.preferredContactMethod) {
            // need to update this contact with the new preferred contact method
            updateContact = true
          }
          return { ...prevEvent, ...event }
        }
        return prevEvent
      })
    )
    await saveEvent({
      variables: {
        event,
        eventTemplate
      },
      refetchQueries: [updateContact ? 'getContact' : null].filter(Boolean)
    })
    onEventsChange?.()
  }

  return (
    <div className={classes.root}>
      <EventTable
        handleCreate={handleCreate}
        title={title}
        loading={loading}
        events={events}
        totalRows={totalRows}
        page={page}
        rowsPerPage={perPage}
        pages={Math.ceil(totalRows / perPage)}
        disablePagination={totalRows <= perPage}
        onPageChange={(e) => {
          setPage(e)
        }}
        onEdit={handleEditEvent}
      />
    </div>
  )
}

const sortEvents = (events: CalendarEvent[]) => {
  const now = moment()

  // Not canceled and in the future (or currently ongoing multi-day events)
  const futureEvents = (events ?? [])
    .filter(
      (event) =>
        !event.canceled &&
        (moment(event.start).isSameOrAfter(now) || (event.end && moment(event.end).isSameOrAfter(now)))
    )
    .sort((a, b) => moment(a.start).diff(moment(b.start)))

  // Not canceled and in the past (started and ended before now)
  const pastEvents = (events ?? [])
    .filter(
      (event) => !event.canceled && moment(event.start).isBefore(now) && (!event.end || moment(event.end).isBefore(now))
    )
    .sort((a, b) => moment(b.start).diff(moment(a.start)))

  // All canceled events
  const canceledEvents = (events ?? [])
    .filter((event) => event.canceled)
    .sort((a, b) => moment(b.start).diff(moment(a.start)))

  return [...futureEvents, ...pastEvents, ...canceledEvents]
}

export default EventDocList
